<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Examples/Website_Integration/Including_Model_Information';
    let nextLink= '/Demo';
    
    import Figure from "$lib/figures/Figure.svelte";
</script>

<PrevNextSection {prevLink} {nextLink} />



<SectionComponent tag="h1" 
  id="adding-the-editor-1"
  bind:intersecting={visibleById["adding-the-editor-1"]}
  onIntersectChange={(visible) => setVisible("adding-the-editor-1", visible)}
>Adding the Editor</SectionComponent>
<p>Now at last, we come to the part you have been waiting for: adding the editor to the webpage.
Because of all the previous work, this is now easy. Add a single line to your Svelte component,
and add a single line to the <code>showUnit</code> function in the <code>WebappConfigurator</code>. The rest of
the <code>showUnit</code> function is merely administration to keep some of the state variables in sync.</p>
<pre class="language-html"><code class="language-html">// IntegrationExample/webapp/src/lib/main-app/WebappLayout.svelte#L64-L64

&lt;FreonComponent editor=&#123;WebappConfigurator.getInstance().editorEnvironment?.editor&#125; /&gt;</code></pre>
<pre class="language-ts"><code class="language-ts">// IntegrationExample/webapp/src/lib/language/WebappConfigurator.ts#L158-L158

this.editorEnvironment.editor.rootElement = newUnit;</code></pre>
<p>But wait, we are forgetting the styling.</p>
<SectionComponent tag="h2" 
  id="css-styling-2"
  bind:intersecting={visibleById["css-styling-2"]}
  onIntersectChange={(visible) => setVisible("css-styling-2", visible)}
>CSS Styling</SectionComponent>
<p>The choice for the <a href="https://flowbite-svelte.com/" target="_blank">Flowbite</a> UI library means that we
must work with <a href="https://tailwindcss.com/" target="_blank">Tailwind</a> as a CSS framework. Furthermore, the
dark mode button provided by Flowbite depends on dark mode being toggled based on
a CSS selector instead of the <code>prefers-color-scheme</code> media query
(see <a href="https://tailwindcss.com/docs/dark-mode#toggling-dark-mode-manually" target="_blank">Tailwind Dark mode</a>).
So we need to adjust our CSS files.</p>
<p>For this we have copied the CSS files from the <code>@freon4dsl/core-svelte</code> package and imported them in
the main CSS file.</p>
<pre class="language-scss"><code class="language-scss">// IntegrationExample/webapp/src/app.css#L5-L8

@import '../static/freon.css';
@import '../static/freon-dark.css';
@import '../static/freon-light.css';</code></pre>
<p>Furthermore, in the CSS for the dark mode every entry has been prefixed with <code>.dark</code>.</p>
<pre class="language-scss"><code class="language-scss">// IntegrationExample/webapp/static/freon-dark.css#L7-L12

.dark .freon-component &#123;
    color: whitesmoke;
    background-color: darkslategray;
    --md-sys-color-primary: whitesmoke;
    --md-sys-color-on-surface-variant: whitesmoke; /* color for the one that is not checked */
&#125;</code></pre>
<SectionComponent tag="h2" 
  id="the-result-3"
  bind:intersecting={visibleById["the-result-3"]}
  onIntersectChange={(visible) => setVisible("the-result-3", visible)}
>The Result</SectionComponent>
<p>Now, drum roll, please! Here is the Freon editor integrated in the webpage of your choice!</p>
<Figure
imageName={'examples/WebsiteIntegration/app-with-editor.png'}
caption={'Webpage showing the Freon editor'}
figureNumber={1}
/>


<PrevNextSection {prevLink} {nextLink} />